import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../services/api/axios";

import { useAuth } from "../../../providers/AuthProvider";
import { addRecentItem } from "../../home-dashboard/RecentItemsHelper";

import BackButton from "../../../shared/components/BackButton";
import StateDropdown from "../../../shared/forms/StateProvinceDropDown";
import CountryDropdown from "../../../shared/forms/CountriesDropDown";
import SectorDropdown from "../../../shared/forms/SectorDropDown";
import {
  formatBroadSectorLabel,
} from "../../bre/companies/BRECompanyHelpers";

import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Divider,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import InboxIcon from "@mui/icons-material/Inbox";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import InputAdornment from "@mui/material/InputAdornment";

import { PRIMARY_BUTTON_SX } from "../../../theme/buttonstyles";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

const STATUS_OPTIONS = ["New", "Converted", "Closed"];

const STATUS_CONFIG = {
  "New":       { bg: "#E3F2FD", color: "#0D47A1", border: "#1976D2" },
  "Converted": { bg: "#E8F5E9", color: "#2E7D32", border: "#43A047" },
  "Closed":    { bg: "#F5F5F5", color: "#616161", border: "#9E9E9E" },
};

function normalizeStatus(status) {
  return STATUS_OPTIONS.includes(status) ? status : "New";
}

function SectionHeader({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
      <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: BRAND_GREEN, flexShrink: 0 }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE }}>
        {children}
      </Typography>
    </Box>
  );
}

function InquiryDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = window.location.pathname.endsWith("/new");
  const safeString = (v) => (typeof v === "string" ? v : "");

  const [loading, setLoading] = useState(!isNew);
  const [inquiry, setInquiry] = useState({
    company_name: "", contact_name: "", email: "", phone: "",
    sector: "", naics_category: "", naics_code: "", city: "", state_province: "",
    country: "", source: "", message: "", status: "New",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (msg, severity = "success") => {
    setSnackbarMsg(msg); setSnackbarSeverity(severity); setSnackbarOpen(true);
  };

  useEffect(() => {
    if (isNew || !id) { setLoading(false); return; }
    setLoading(true);
    api.get(`/inquiries/${id}`)
      .then((res) => {
        const data = res.data || {};
        setInquiry({
          company_name: data.company_name || "", contact_name: data.contact_name || "",
          email: data.email || "", phone: data.phone || "",
          sector: data.sector || "", naics_category: data.naics_category || "", naics_code: data.naics_code || "",
          city: data.city || "", state_province: data.state_province || "",
          country: data.country || "", source: data.source || "",
          message: data.message || "", status: normalizeStatus(data.status),
        });
      })
      .catch(() => showSnackbar("Failed to load inquiry", "error"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  useEffect(() => {
    if (!user?.id || isNew || !id || !inquiry?.company_name) return;
    addRecentItem(user.id, "inquiry", { id: Number(id), name: inquiry.company_name });
  }, [user?.id, id, isNew, inquiry?.company_name]);

  const handleChange = (field) => (e) => setInquiry((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSectorChange = (e) => {
    setInquiry((prev) => ({ ...prev, sector: e.target.value }));
  };

  const handleStatusChange = async (status) => {
    try {
      await api.put(`/inquiries/${id}`, { status });
      setInquiry((prev) => ({ ...prev, status }));
    } catch {
      showSnackbar("Failed to update status", "error");
    }
  };

  const handleSubmit = () => {
    const payload = {
      company_name: inquiry.company_name || null,
      contact_name: inquiry.contact_name || null,
      email: inquiry.email || null,
      phone: inquiry.phone || null,
      sector: inquiry.sector || null,
      naics_category: inquiry.naics_category || null,
      naics_code: inquiry.naics_code?.trim() || null,
      city: inquiry.city || null,
      state_province: inquiry.state_province || null,
      country: inquiry.country || null,
      source: inquiry.source || null,
      message: inquiry.message || null,
      status: inquiry.status || "New",
    };

    const request = isNew
      ? api.post("/inquiries", payload)
      : api.put(`/inquiries/${id}`, payload);

    request
      .then(() => navigate("/inquiries"))
      .catch(() => showSnackbar("Failed to save inquiry", "error"));
  };

  if (loading) return (
    <Box sx={{ p: 4, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <Typography sx={{ color: BRAND_BLUE, fontWeight: 600 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2, minHeight: "100%", boxSizing: "border-box", overflowX: "hidden", bgcolor: "#f0f4f8" }}>
      <Box sx={{ maxWidth: 1850, mx: "auto", width: "100%" }}>

        {/* HEADER CARD */}
        <Paper elevation={0} sx={{ mb: 2, borderRadius: 3, overflow: "hidden", border: "1px solid rgba(0,51,108,0.10)", boxShadow: "0 2px 12px rgba(0,51,108,0.08)", bgcolor: "white" }}>
          <Box sx={{ height: 4, background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_GREEN})` }} />

          <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
              <BackButton to="/inquiries" />
              {user?.organization?.name && (
                <Chip label={user.organization.name} size="small"
                  sx={{ bgcolor: "rgba(0,51,108,0.06)", color: BRAND_BLUE, fontWeight: 600, fontSize: 12, border: "1px solid rgba(0,51,108,0.12)" }}
                />
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 46, height: 46, borderRadius: 2, bgcolor: "rgba(0,51,108,0.07)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <InboxIcon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ color: BRAND_BLUE, fontWeight: 700, lineHeight: 1.2 }}>
                  {isNew ? "New Inquiry" : inquiry.company_name || "Untitled Inquiry"}
                </Typography>
                {!isNew && inquiry.contact_name && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                    <PersonIcon sx={{ fontSize: 14, color: BRAND_GREEN }} />
                    <Typography variant="body2" sx={{ color: BRAND_GREEN }}>{inquiry.contact_name}</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Status chips */}
            {!isNew && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}>
                  Status
                </Typography>
                {STATUS_OPTIONS.map((s) => {
                  const cfg = STATUS_CONFIG[s];
                  const isActive = normalizeStatus(inquiry.status) === s;
                  return (
                    <Chip
                      key={s}
                      label={s}
                      size="small"
                      onClick={() => handleStatusChange(s)}
                      sx={{
                        cursor: "pointer",
                        fontWeight: isActive ? 700 : 500,
                        fontSize: 12,
                        bgcolor: isActive ? cfg.bg : "transparent",
                        color: isActive ? cfg.color : "text.secondary",
                        border: `1.5px solid ${isActive ? cfg.border : "rgba(0,0,0,0.15)"}`,
                        transition: "all 0.15s ease",
                        "&:hover": { bgcolor: cfg.bg, color: cfg.color, border: `1.5px solid ${cfg.border}` },
                      }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>

          <Divider />
        </Paper>

        {/* CONTENT CARD */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,51,108,0.10)", boxShadow: "0 2px 12px rgba(0,51,108,0.08)", p: 3, bgcolor: "white" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>

            {/* General */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
              <SectionHeader>General</SectionHeader>
              <Stack spacing={3}>
                <TextField label="Company Name" value={safeString(inquiry.company_name)} onChange={handleChange("company_name")} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><BusinessIcon sx={{ color: BRAND_BLUE, fontSize: 20 }} /></InputAdornment> }}
                />
                <TextField label="Contact Name" value={safeString(inquiry.contact_name)} onChange={handleChange("contact_name")} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: BRAND_BLUE, fontSize: 20 }} /></InputAdornment> }}
                />
                <TextField label="Email" value={safeString(inquiry.email)} onChange={handleChange("email")} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: BRAND_GREEN, fontSize: 20 }} /></InputAdornment> }}
                />
                <TextField label="Phone" value={safeString(inquiry.phone)} onChange={handleChange("phone")} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: BRAND_BLUE, fontSize: 20 }} /></InputAdornment> }}
                />
              </Stack>
            </Box>

            {/* Business Details */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
              <SectionHeader>Business Details</SectionHeader>
              <Stack spacing={3}>
                <SectorDropdown
                  label="Sector"
                  value={inquiry.sector}
                  onChange={handleSectorChange}
                  formatOptionLabel={formatBroadSectorLabel}
                  sx={{
                    "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused, & .MuiInputBase-input, & .MuiSelect-icon": {
                      color: BRAND_BLUE,
                    },
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { "& .MuiMenuItem-root": { color: BRAND_BLUE } },
                      },
                    },
                  }}
                  fullWidth
                />
                <TextField label="NAICS Code" value={safeString(inquiry.naics_code)} onChange={handleChange("naics_code")} fullWidth />
                <TextField label="NAICS Text" value={safeString(inquiry.naics_category)} onChange={handleChange("naics_category")} fullWidth />
                <TextField label="Source" value={safeString(inquiry.source)} onChange={handleChange("source")} fullWidth />
                <TextField label="Notes" value={safeString(inquiry.message)} onChange={handleChange("message")} multiline minRows={3} maxRows={6} fullWidth />
              </Stack>
            </Box>

            {/* Location */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
              <SectionHeader>Location</SectionHeader>
              <Stack spacing={3}>
                <CountryDropdown label="Country" value={safeString(inquiry.country)} onChange={handleChange("country")} fullWidth />
                <StateDropdown label="State / Province" country={inquiry.country} value={safeString(inquiry.state_province)} onChange={handleChange("state_province")} fullWidth />
                <TextField label="City" value={safeString(inquiry.city)} onChange={handleChange("city")} fullWidth />
              </Stack>
            </Box>

          </Box>

          {/* Save button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, pt: 2.5, borderTop: "1px solid rgba(0,51,108,0.08)" }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} sx={PRIMARY_BUTTON_SX}>
              {isNew ? "Create Inquiry" : "Save Changes"}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)} variant="filled">
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default InquiryDetails;
